import { ForwardedRef, forwardRef, PropsWithChildren, useMemo } from 'react';
import classNames from 'classnames';

import { useMergeRefs } from '@xemplo/ref-utils';

import { ListMode, TagCreateFn } from '../../utils/types/stateful-list.types';
import { AddTag } from '../add-tag/add-tag';
import * as S from '../common/scroll-area.styles';
import { ItemListRenderer } from '../item-list-renderer/item-list-renderer';

import { ItemListWrapperProps } from './item-list-wrapper.types';
import { useItemListWrapper } from './use-item-list';

export const ItemListWrapper = forwardRef(function (
  props: PropsWithChildren<ItemListWrapperProps>,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  const {
    children,
    mode,
    renderItems,
    activeDescendantId,
    containerRef,
    onKeyDown,
    onTagCreate,
    id,
  } = props;
  const { style, nonScrollRef, contentRef } = useItemListWrapper(props);

  // Merging the containerRef with the forwardedRef. The forwardedRef will be needed
  // for the popover implementation so it can receive the positioning correctly.
  // the containerRef comes from the useKeyboardNavigation hook and is needed
  // so the hook can trap the focus inside the list. Merging allow both refs to work together.
  // without conflict.
  const wrapperRef = useMergeRefs<HTMLDivElement>([containerRef, forwardedRef]);

  const removeBorderRadius = useMemo(() => {
    return Array.isArray(children)
      ? children.every((child) => child !== false)
      : children;
  }, [children]);

  assertTagModeProps(mode, onTagCreate);

  return (
    <S.ListWrapper
      id={id}
      ref={wrapperRef}
      role="listbox"
      aria-multiselectable={mode === ListMode.Multi || mode === ListMode.Tags}
      aria-activedescendant={activeDescendantId}
      onKeyDown={onKeyDown}
      style={{ ...style, ...props.style }}
      className={classNames(props.className)}
    >
      <div ref={nonScrollRef}>{children}</div>
      <S.ScrollAreaRoot>
        <S.ScrollAreaViewport className={removeBorderRadius ? 'no-border-radius' : ''}>
          <S.ScrollAreaContent ref={contentRef}>
            <ItemListRenderer groups={renderItems.groups} mode={mode} />
          </S.ScrollAreaContent>
        </S.ScrollAreaViewport>
        <S.ScrollAreaScrollbar>
          <S.ScrollAreaThumb />
        </S.ScrollAreaScrollbar>
      </S.ScrollAreaRoot>
      {mode === ListMode.Tags && <AddTag />}
    </S.ListWrapper>
  );
});

/**
 * Asserts that the `onTagCreate` prop is provided when the mode is set to "tags".
 * This is to avoid unnecessary null checks in the component body as TS is not
 * able to automatically infer this relationship.
 * @param mode The current list mode.
 * @param onTagCreate The onTagCreate prop.
 */
function assertTagModeProps(
  mode: ListMode,
  onTagCreate: ((tag: string) => void) | undefined
): asserts onTagCreate is TagCreateFn {
  if (mode === ListMode.Tags && onTagCreate == null) {
    throw new Error('onTagCreate prop is required when mode is set to "tags"');
  }
}
